import React, { useEffect, useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth, db } from "../firebase";
import { addDoc, collection, doc, getDoc, Timestamp } from "firebase/firestore";
import Swal from "sweetalert2";
import { Box, Button, TextField, Typography, Paper, CircularProgress } from "@mui/material";

function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [systemDetails, setSystemDetails] = useState({});
    const [loading, setLoading] = useState(false);

    const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
    });

    useEffect(() => {
        setSystemDetails({
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            screenWidth: window.screen.width,
            screenHeight: window.screen.height,
            language: navigator.language,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
    }, []);

    const login1 = async () => {
        if (!email || !password) {
            return Toast.fire({
                icon: "error",
                title: "Please enter your email and password",
            });
        }

        setLoading(true);
        try {
            await signInWithEmailAndPassword(auth, email, password);
            const user = auth.currentUser;

            if (user) {
                const docRef = doc(db, "authroles", user.uid);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists() && docSnap.data().role === "SUPERADMIN") {
                    Toast.fire({ icon: "success", title: "Login Successful!" });

                    await addDoc(collection(db, "logindetails"), {
                        uid: user.uid,
                        username: docSnap.data().name,
                        systemDetails,
                        createdTime: Timestamp.now(),
                    });

                    window.location.href = "/home";
                } else {
                    Toast.fire({ icon: "error", title: "Access Denied" });
                    auth.signOut();
                }
            }
        } catch (error) {

            console.log(error)
            Toast.fire({ icon: "error", title: error + "Invalid credentials!" });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                height: "100vh",
                background: `linear-gradient(180deg, #1a2a4f 0%, #0f3d3e 100%)`,
                position: "relative",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                overflow: "hidden",
            }}
        >
            {/* Abstract background elements */}
            <Box
                sx={{
                    position: "absolute",
                    width: 500,
                    height: 500,
                    borderRadius: "50%",
                    background: "rgba(212,175,55,0.15)",
                    top: "-120px",
                    left: "-150px",
                    filter: "blur(150px)",
                    transform: "rotate(45deg)",
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    width: 400,
                    height: 400,
                    borderRadius: "50%",
                    background: "rgba(253,246,227,0.1)",
                    bottom: "-100px",
                    right: "-100px",
                    filter: "blur(120px)",
                    transform: "rotate(-30deg)",
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    width: 600,
                    height: 600,
                    clipPath: "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)",
                    background: "rgba(212,175,55,0.05)",
                    top: "30%",
                    left: "40%",
                    filter: "blur(180px)",
                    transform: "rotate(15deg)",
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    width: 700,
                    height: 700,
                    borderRadius: "50%",
                    background: "rgba(253,246,227,0.07)",
                    top: "-200px",
                    right: "-250px",
                    filter: "blur(200px)",
                }}
            />

            {/* Login Card */}
            <Paper
                elevation={8}
                sx={{
                    width: "90%",
                    maxWidth: 400,
                    p: 5,
                    borderRadius: 4,
                    textAlign: "center",
                    backdropFilter: "blur(15px)",
                    background: "rgba(15,61,62,0.85)", // dark teal glass
                    color: "#fdf6e3", // cream text
                    border: "1px solid rgba(212,175,55,0.4)",
                    zIndex: 1,
                }}
            >
                <img
                    src="/logo.png"
                    alt="Travel To Xplore Logo"
                    style={{
                        width: 90,
                        height: 90,
                        borderRadius: "50%",
                        background: "rgba(212,175,55,0.15)",
                        padding: 10,
                        boxShadow: "0 0 20px rgba(212,175,55,0.5)",
                        marginBottom: 20,
                    }}
                />
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Travel To Xplore
                </Typography>
                <Typography variant="body2" sx={{ mb: 3, opacity: 0.85 }}>
                    Sign in to start your session
                </Typography>

                <TextField
                    label="Email"
                    type="email"
                    variant="filled"
                    fullWidth
                    sx={{
                        mb: 3,
                        input: { color: "#fdf6e3" },
                        label: { color: "rgba(253,246,227,0.8)" },
                        "& .MuiFilledInput-root": {
                            background: "rgba(212,175,55,0.1)",
                            borderRadius: 2,
                        },
                        "& .MuiFilledInput-root:before": { borderBottom: "none" },
                    }}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                />

                <TextField
                    label="Password"
                    type="password"
                    variant="filled"
                    fullWidth
                    sx={{
                        mb: 4,
                        input: { color: "#fdf6e3" },
                        label: { color: "rgba(253,246,227,0.8)" },
                        "& .MuiFilledInput-root": {
                            background: "rgba(212,175,55,0.1)",
                            borderRadius: 2,
                        },
                        "& .MuiFilledInput-root:before": { borderBottom: "none" },
                    }}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                />

                <Button
                    variant="contained"
                    sx={{
                        py: 1.3,
                        borderRadius: 5,
                        fontWeight: "bold",
                        textTransform: "none",
                        backgroundColor: "#d4af37",
                        color: "#1a2a4f",
                        "&:hover": {
                            backgroundColor: "#c9b37e",
                            transform: "scale(1.05)",
                        },
                        boxShadow: "0 0 25px rgba(212,175,55,0.6)",
                        transition: "all 0.3s ease",
                    }}
                    fullWidth
                    onClick={login1}
                    disabled={loading}
                >
                    {loading ? <CircularProgress size={24} color="inherit" /> : "Sign In"}
                </Button>

                <Typography variant="caption" display="block" sx={{ mt: 4, opacity: 0.6 }}>
                    © {new Date().getFullYear()} Travel To Xplore Panel
                </Typography>
            </Paper>
        </Box>
    );
}

export default Login;
